/**
 * Global Download Manager
 * Tracks all active downloads across the application
 */

// Store active downloads with metadata
const activeDownloadsMap = new Map();

/**
 * Register a download
 */
export function registerDownload(downloadId, metadata) {
  activeDownloadsMap.set(downloadId, {
    ...metadata,
    id: downloadId,
    status: metadata.status || "downloading",
    progress: metadata.progress || 0,
    startTime: metadata.startTime || Date.now(),
  });
  notifyListeners();
}

/**
 * Update download progress
 */
export function updateDownloadProgress(downloadId, progress, status = "downloading") {
  const download = activeDownloadsMap.get(downloadId);
  if (download) {
    download.progress = progress;
    download.status = status;
    download.lastUpdate = Date.now();
    notifyListeners();
  }
}

/**
 * Complete a download
 */
export function completeDownload(downloadId, success = true) {
  const download = activeDownloadsMap.get(downloadId);
  if (download) {
    console.log(`[download-manager] Completing download: ${downloadId}, success: ${success}, current status: ${download.status}`);
    
    // Immediately remove completed downloads - don't store them
    console.log(`[download-manager] Removing completed download immediately: ${downloadId}`);
    activeDownloadsMap.delete(downloadId);
    notifyListeners();
  } else {
    console.warn(`[download-manager] Attempted to complete download that doesn't exist: ${downloadId}`);
    // Try to find by filename or other metadata
    const allDownloads = Array.from(activeDownloadsMap.values());
    const matchingDownload = allDownloads.find(d => 
      d.filename === downloadId || 
      d.id === downloadId ||
      d.studyId === downloadId
    );
    if (matchingDownload) {
      console.log(`[download-manager] Found matching download by metadata, removing: ${matchingDownload.id}`);
      activeDownloadsMap.delete(matchingDownload.id);
      notifyListeners();
    }
  }
}

/**
 * Remove a download
 */
export function removeDownload(downloadId) {
  activeDownloadsMap.delete(downloadId);
  notifyListeners();
}

/**
 * Get all active downloads
 */
export function getActiveDownloads() {
  return Array.from(activeDownloadsMap.values());
}

/**
 * Get count of active downloads (downloading or pending)
 */
export function getActiveDownloadsCount() {
  return Array.from(activeDownloadsMap.values()).filter(
    (d) => d.status === "downloading" || d.status === "pending"
  ).length;
}

/**
 * Get count of all downloads (including completed/failed)
 */
export function getAllDownloadsCount() {
  return activeDownloadsMap.size;
}

// Listeners for state changes
const listeners = new Set();

/**
 * Subscribe to download state changes
 */
export function subscribe(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Notify all listeners of state changes
 */
function notifyListeners() {
  listeners.forEach((listener) => {
    try {
      listener(getActiveDownloads());
    } catch (error) {
      console.error("Error in download listener:", error);
    }
  });
}

